import "@emotion/react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { SavedBrandContext } from "contexts/SavedBrand";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { useContext, useState } from "react";
import { routes } from "schemas/routes";

import { isPage } from "utils/general";
import { trackEvent } from "utils/tracking";

import Collections from "./Collections";
import MoveMenu from "./Collections/MoveMenu";
import NewCollectionDialog from "./Collections/NewCollectionDialog";
import ListHeader from "./ListHeader";
import ProgressBar from "./ProgressBar";
import FavoritesListMobile from "./mobile";
import styles from "./styles";

interface FavoritesListProps {
  closeDialog: () => void;
}

export default function FavoritesList({ closeDialog }: FavoritesListProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const {
    selectedCollectionId,
    setSelectedCollectionId,
    selectedCollection,
    getCollectionBrands,
    fetchCollections,
    currentTab,
    refreshSavedList,
    refreshLoading,
  } = useContext(SavedBrandCollectionsContext);
  const { setBulkBrands, setSelectAll } = useContext(SavedBrandContext);

  const [openCollectionDialog, setOpenCollectionDialog] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);

  const toggleBulkMode = () => {
    if (selectedCollectionId === null) return;
    trackEvent("Favorites List - Select Button Clicked", {
      "Collection ID": selectedCollectionId,
    });
    if (isBulkMode) {
      setBulkBrands([]);
      setSelectAll(false);
    }
    setIsBulkMode((prev) => !prev);
  };

  const handleBack = () => {
    setSelectedCollectionId(null);
    setIsBulkMode(false);
    setBulkBrands([]);
    fetchCollections();
  };

  const editCollection = () => {
    if (selectedCollectionId === null) return;
    trackEvent("Favorites List - Edit Collection Button Clicked", {
      "Collection ID": selectedCollectionId,
    });
    setOpenCollectionDialog(true);
  };

  const handleCreateNewList = (bulkMove?: boolean) => {
    trackEvent("Favorites List - New List Button Clicked", {
      source: bulkMove ? "Move To Menu" : "New List Button",
    });
    setOpenCollectionDialog(true);
  };

  const displayedBrands =
    selectedCollectionId !== null
      ? getCollectionBrands(selectedCollectionId, currentTab)?.map(
          (x) => x.bentoBrand,
        )
      : [];

  const collections = (
    <Collections
      isBulkMode={isBulkMode}
      handleCreateNewList={handleCreateNewList}
      displayedBrands={displayedBrands}
    />
  );

  const header = (
    <ListHeader
      toggleBulkMode={toggleBulkMode}
      isBulkMode={isBulkMode}
      displayedBrands={displayedBrands}
    />
  );

  const footer = (
    <Grid
      container
      justifyContent={isBulkMode ? "flex-end" : "center"}
      sx={styles.footer}
    >
      {isBulkMode ? (
        <MoveMenu
          handleCreateNewList={handleCreateNewList}
          setIsBulkMode={setIsBulkMode}
          currentTab={currentTab}
        />
      ) : (
        <ProgressBar />
      )}
    </Grid>
  );

  return (
    <>
      {isMobileScreen ? (
        <>
          {!isPage(routes.inboxCheckins) &&
            !isPage(routes.inboxScheduled) &&
            !isPage(routes.virtualAssistant) && (
              <FavoritesListMobile
                collections={collections}
                selectedCollection={selectedCollection}
                handleBack={handleBack}
                header={header}
                footer={footer}
              />
            )}
        </>
      ) : (
        <Box>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={styles.desktopHeader}
          >
            <Grid container item xs={9} alignItems="center">
              {selectedCollection && (
                <IconButton sx={styles.close} onClick={handleBack}>
                  <Box component="i" className="fa-regular fa-arrow-left" />
                </IconButton>
              )}

              <Typography variant="h6" component="div" sx={{ fontSize: 14 }}>
                {selectedCollection?.name || "SAVED BRANDS"}
                {!selectedCollection?.name && (
                  <IconButton
                    onClick={refreshSavedList}
                    sx={{ color: "black", fontSize: 12 }}
                  >
                    {refreshLoading ? (
                      <CircularProgress size={10} />
                    ) : (
                      <Box component="i" className="fa-regular fa-refresh" />
                    )}
                  </IconButton>
                )}
              </Typography>
              <Box>
                {!!selectedCollection?.id &&
                selectedCollection.id > 0 &&
                !selectedCollection.isInstagramCollection ? (
                  <Tooltip title="Edit Collection">
                    <IconButton sx={styles.close} onClick={editCollection}>
                      <Box component="i" className="fa-regular fa-edit" />
                    </IconButton>
                  </Tooltip>
                ) : null}

                {!!selectedCollection?.id &&
                  selectedCollection.isInstagramCollection && (
                    <Box
                      component="i"
                      className="fa-brands fa-instagram"
                      sx={{ color: "#E4405F", ml: 0.5, fontSize: "0.875rem" }}
                    />
                  )}
              </Box>
            </Grid>

            {!isMobileScreen && (
              <Grid container item xs justifyContent="flex-end">
                <IconButton sx={styles.close} onClick={closeDialog}>
                  <Box component="i" className="fa-regular fa-xmark" />
                </IconButton>
              </Grid>
            )}
          </Grid>

          {header}
          {collections}
          {footer}
        </Box>
      )}

      <NewCollectionDialog
        open={openCollectionDialog}
        handleClose={() => setOpenCollectionDialog(false)}
        isBulkMode={isBulkMode}
        setIsBulkMode={setIsBulkMode}
      />
    </>
  );
}
